/**
 * Cascade Worker API client.
 *
 * In dev, requests hit `/api/...` and Vite proxies to the local Worker
 * (port 8789). In prod, set VITE_API_BASE_URL to the deployed Cascade Worker
 * URL. Typed endpoint wrappers land with the read-only V1 pass; only the
 * transport lives here for now.
 */

import { authedFetch } from '../auth/supabase-browser';

const configuredBaseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const FALLBACK_PROD_API_BASE_URL = 'https://admin-cascade-api.dakota-276.workers.dev';
const BASE_URL = configuredBaseUrl || (import.meta.env.PROD ? FALLBACK_PROD_API_BASE_URL : '');

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

/**
 * A server-paged list. `total` is the size of the full filtered result set, not
 * of `items` — the Worker filters and sorts before slicing, so the count stays
 * correct while the payload stays bounded.
 */
export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error?.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body.error?.code ?? 'unknown_error';
    this.details = body.error?.details;
  }
}

export function apiUrl(path: string): string {
  return /^https?:\/\//i.test(path) ? path : `${BASE_URL}${path}`;
}

export function authorizedFetch(path: string, init?: RequestInit): Promise<Response> {
  return authedFetch(apiUrl(path), { ...init, credentials: 'include' });
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  const response = await authorizedFetch(path, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiClientError(response.status, body);
  }

  return (await response.json()) as T;
}
